import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { previewTypographyCss } from '../../pages/Typography/typographyApi';
import { PREVIEW_FRAME_NAME, PREVIEW_HTML_CLASS } from '../../utils/typographyPreview';
import { attachRoleLabels } from './roleLabels';

// A real public page, in an iframe at a true device width, with UNPUBLISHED
// typography applied. Used by the editor's side panel and the full-size
// /dashboard/typography/preview page.
//
// The frame is named PREVIEW_FRAME_NAME so the public app renders inside it
// despite the admin login (utils/typographyPreview.js). `settings` ({ roles,
// groups }) are rendered to CSS by the server — the same validation and renderer
// as publishing — and injected into the frame, outranking the published block.
// The frame's own app tags its page group, so group overrides apply by themselves.

export const PREVIEW_PAGES = [
  { label: 'Home', path: '/home' },
  { label: 'Purchase', path: '/purchase' },
  { label: 'Refinance', path: '/refinance' },
  { label: 'Loan Programs', path: '/loan-programs' },
  { label: 'VA Loans (program page)', path: '/va' },
  { label: 'Find a Loan Officer', path: '/find-a-loan-officer' },
  { label: 'Contact Us', path: '/contact' },
  { label: 'Blog', path: '/blog' },
  { label: 'Calculators', path: '/calculators' },
  { label: 'Careers', path: '/team-jet' },
  { label: 'Privacy Policy', path: '/privacy' },
];

const FRAME = {
  desktop: { width: 1280, height: 800 },
  mobile: { width: 390, height: 844 },
};

const STYLE_ID = 'jet-typography-preview-css';

export default function SitePreviewFrame({
  settings,
  path = '/home',
  device = 'desktop',
  maxHeight,
  onErrors,
  showLabels = false,
  roles = [],
  onRoleClick,
}) {
  const outerRef = useRef(null);
  const frameRef = useRef(null);
  const cssRef = useRef('');
  const sentRef = useRef('');
  const scaleRef = useRef(1);
  const labelsCleanupRef = useRef(null);
  const [scale, setScale] = useState(0);
  const [loadCount, setLoadCount] = useState(0);
  const frame = FRAME[device] || FRAME.desktop;
  scaleRef.current = scale || 1;

  // Put the latest CSS into the frame. Also runs on every frame load, since a
  // full navigation inside the frame replaces its document.
  const inject = useCallback(() => {
    const doc = frameRef.current?.contentDocument;
    if (!doc?.head) return;
    doc.documentElement.classList.add(PREVIEW_HTML_CLASS);
    let el = doc.getElementById(STYLE_ID);
    if (!el) {
      el = doc.createElement('style');
      el.id = STYLE_ID;
      doc.head.appendChild(el);
    }
    el.textContent = cssRef.current;
  }, []);

  // Render values to CSS, debounced — the editor streams edits as you type.
  useEffect(() => {
    if (!settings) return undefined;
    const body = JSON.stringify(settings);
    if (body === sentRef.current) return undefined;
    const t = setTimeout(async () => {
      sentRef.current = body;
      const res = await previewTypographyCss(settings);
      if (res?.status === 200) {
        cssRef.current = res.data;
        inject();
        if (onErrors) onErrors([]);
      } else {
        let data = res?.data;
        try {
          data = typeof data === 'string' ? JSON.parse(data) : data;
        } catch (e) {
          data = null;
        }
        // Keep showing the last valid preview.
        if (onErrors) onErrors(data?.errors || [data?.message || 'Could not render the preview']);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [settings, inject, onErrors]);

  const onFrameLoad = useCallback(() => {
    inject();
    setLoadCount((n) => n + 1); // a new document — role labels re-attach to it
  }, [inject]);

  // Role labels ("H1 · 52px") drawn inside the frame; re-attached whenever the
  // frame loads a new document, the on-screen scale changes (tag size), or the
  // role list / click handler changes.
  useEffect(() => {
    labelsCleanupRef.current?.();
    labelsCleanupRef.current = null;
    if (showLabels && roles.length && frameRef.current && loadCount > 0) {
      labelsCleanupRef.current = attachRoleLabels(frameRef.current, {
        roles,
        getScale: () => scaleRef.current,
        onRoleClick,
      });
    }
    return () => {
      labelsCleanupRef.current?.();
      labelsCleanupRef.current = null;
    };
  }, [showLabels, roles, onRoleClick, loadCount, scale]);

  // Fit the true-size frame into the available width (and height, if capped).
  // Tiny changes are ignored so a scrollbar appearing/disappearing elsewhere
  // can't start a resize feedback loop.
  useLayoutEffect(() => {
    const outer = outerRef.current;
    if (!outer) return undefined;
    const measure = () => {
      if (outer.clientWidth <= 0) return;
      const next = Math.min(1, outer.clientWidth / frame.width, maxHeight ? maxHeight / frame.height : 1);
      setScale((cur) => (Math.abs(cur - next) > 0.01 ? next : cur));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(outer);
    return () => ro.disconnect();
  }, [frame.width, frame.height, maxHeight]);

  return (
    <Box ref={outerRef} sx={{ width: '100%' }}>
      <Box
        sx={{
          width: frame.width * scale,
          height: frame.height * scale,
          mx: 'auto',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: '#fff',
        }}
      >
        {scale > 0 && (
          <iframe
            key={`${device}:${path}`}
            ref={frameRef}
            name={PREVIEW_FRAME_NAME}
            title="Typography preview"
            src={path}
            onLoad={onFrameLoad}
            style={{
              width: frame.width,
              height: frame.height,
              border: 0,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              display: 'block',
            }}
          />
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
        {frame.width}px wide{scale > 0 && scale < 1 ? ` · shown at ${Math.round(scale * 100)}%` : ''} · click around
        freely; forms are disabled
      </Typography>
    </Box>
  );
}

SitePreviewFrame.propTypes = {
  settings: PropTypes.object,
  path: PropTypes.string,
  device: PropTypes.oneOf(['desktop', 'mobile']),
  maxHeight: PropTypes.number,
  onErrors: PropTypes.func,
  showLabels: PropTypes.bool,
  roles: PropTypes.array,
  onRoleClick: PropTypes.func,
};
